import { useState } from 'react'
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  Archive,
  ArrowLeft,
  CalendarClock,
  CalendarX,
  CircleX,
  Pencil,
  RefreshCw,
  Send,
  Trash2,
} from 'lucide-react'
import {
  getV1MeRetrieveQueryOptions,
  getV1QuizAdminPushQuizzesListQueryKey,
  getV1QuizAdminPushQuizzesResultsRetrieveQueryKey,
  getV1QuizAdminPushQuizzesRetrieveQueryKey,
  useV1QuizAdminCapabilities,
  useV1QuizAdminPushQuizzesArchiveCreate,
  useV1QuizAdminPushQuizzesCancelCreate,
  useV1QuizAdminPushQuizzesCancelScheduleCreate,
  useV1QuizAdminPushQuizzesDestroy,
  useV1QuizAdminPushQuizzesDistributionPreviewRetrieve,
  useV1QuizAdminPushQuizzesPartialUpdate,
  useV1QuizAdminPushQuizzesPreviewRetrieve,
  useV1QuizAdminPushQuizzesPublishCreate,
  useV1QuizAdminPushQuizzesResultsRetrieve,
  useV1QuizAdminPushQuizzesRetrieve,
  useV1QuizAdminPushQuizzesRetryNotificationsCreate,
  useV1QuizAdminPushQuizzesScheduleCreate,
  useV1QuizAdminPushQuizzesUpdate,
} from '@/api/client'
import type { AdminPushQuizPreview, AdminPushQuizWriteRequest } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ConfirmDialog } from '@/components/admin-accounts/confirm-dialog'
import { PushQuizStateBadge } from '@/components/push-quiz/push-quiz-state-badge'
import { PushQuizCancelDialog } from '@/components/push-quiz/push-quiz-cancel-dialog'
import { DistributionWarnings } from '@/components/push-quiz/distribution-warnings'
import { PushQuizNotificationStatusBadge } from '@/components/push-quiz/push-quiz-notification-status-badge'
import { PushQuizReminderStatusBadge } from '@/components/push-quiz/push-quiz-reminder-status-badge'
import { PushQuizForm } from '@/components/push-quiz/push-quiz-form'
import { QuizCapabilityNotice } from '@/components/quiz/quiz-capability-notice'
import {
  canAuthorQuiz,
  canPublishQuiz,
  quizCapabilitiesOrClosed,
} from '@/components/quiz/quiz-capabilities'
import {
  PushQuizPreviewDialog,
  type PushQuizPreviewQuestion,
} from '@/components/push-quiz/push-quiz-preview-dialog'
import { pushQuizActionError } from '@/components/push-quiz/push-quiz-error'
import { fromQuiz, parseOffendingUids } from '@/components/push-quiz/push-quiz-form-schema'
import { ResultsCard } from '@/components/push-quiz/results-card'
import {
  pushQuizScoringConfigLabel,
  pushQuizScoringImpactLabel,
} from '@/components/push-quiz/push-quiz-scoring-labels'
import { failureBreakdownSummary } from '@/components/notifications/notification-labels'
import { formatPolishDateTime, toWarsawDateTimeLocal } from '@/lib/push-quiz-dates'
import { canRetryPushQuizNotifications, scopeLabel } from '@/lib/push-quiz-labels'
import { difficultyLabel, typeLabel } from '@/lib/quiz-labels'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_panel/zadania/$uid')({
  beforeLoad: async ({ context }) => {
    // Operator-only area (admin + superadmin): everyone else is bounced out
    // before any quiz data loads (FR-001/FR-002).
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: PushQuizDetailPage,
})

type ActionKind =
  | 'publish'
  | 'schedule'
  | 'cancelSchedule'
  | 'cancel'
  | 'retryNotifications'
  | 'archive'
  | 'delete'
  | null

/**
 * US3/US4 detail (T015/T017/T019): quiz data + the ordered set with pinned
 * revision numbers, the state-machine lifecycle actions behind ConfirmDialogs
 * (schedule pins; publish pins + notifies; cancellation and archive are final; delete is
 * draft-only) and the
 * aggregate results section for published/archived/cancelled quizzes.
 *
 * The affordance matrix IS the state machine (SC-004): draft → schedule /
 * publish / delete; scheduled → cancel scheduling; published → archive/cancel;
 * terminal states → nothing.
 * While `state` is still `undefined` (retrieve in flight) NO affordance
 * renders. Draft/published editing (US2, T016) plugs into this page.
 */
function PushQuizDetailPage() {
  const { uid } = Route.useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const capabilitiesQuery = useV1QuizAdminCapabilities()
  const capabilities = quizCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const canAuthor = canAuthorQuiz(capabilities)
  const publishingEnabled = canPublishQuiz(capabilities)

  const query = useV1QuizAdminPushQuizzesRetrieve(uid, {
    query: {
      refetchInterval: (currentQuery) => {
        const response = currentQuery.state.data
        if (response?.status !== 200) return false
        if (response.data.state === 'scheduled') return 15_000
        if (response.data.reminder_status === 'scheduled') return 15_000
        return response.data.notification_status === 'pending' ||
          response.data.notification_status === 'inProgress' ||
          response.data.reminder_status === 'inProgress'
          ? 3_000
          : false
      },
    },
  })
  const quiz = query.data?.status === 200 ? query.data.data : undefined
  const state = quiz?.state

  // POSITIVE gate (plan/T019): `state` is optional in the generated model, so a
  // negative `state !== 'draft'` gate would fire the results GET for every quiz
  // while the retrieve is in flight — a guaranteed 404 for drafts (contract P6).
  const resultsEnabled = state === 'published' || state === 'archived' || state === 'cancelled'
  const resultsQuery = useV1QuizAdminPushQuizzesResultsRetrieve(uid, {
    query: { enabled: resultsEnabled },
  })
  const results = resultsQuery.data?.status === 200 ? resultsQuery.data.data : undefined
  const resultsFailed =
    resultsQuery.isError || (resultsQuery.data !== undefined && resultsQuery.data.status !== 200)

  const [editing, setEditing] = useState(false)
  const [action, setAction] = useState<ActionKind>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [scheduledAtInput, setScheduledAtInput] = useState('')
  // Publish refusals naming retired/unknown questions mark the matching set
  // rows (FR-017) — kept after the dialog closes so the operator sees them.
  const [offendingUids, setOffendingUids] = useState<string[]>([])

  const publishMutation = useV1QuizAdminPushQuizzesPublishCreate()
  const scheduleMutation = useV1QuizAdminPushQuizzesScheduleCreate()
  const cancelScheduleMutation = useV1QuizAdminPushQuizzesCancelScheduleCreate()
  const cancelMutation = useV1QuizAdminPushQuizzesCancelCreate()
  const previewQuery = useV1QuizAdminPushQuizzesPreviewRetrieve(uid, {
    query: { enabled: state === 'draft' },
  })
  // Advisory pre-flight (feature 080, A13): an empty audience and a colliding
  // quiz were both invisible until after the push had gone out. Fetched only
  // while the operator is actually about to commit a distribution.
  const distributionQuery = useV1QuizAdminPushQuizzesDistributionPreviewRetrieve(uid, {
    query: { enabled: action === 'publish' || action === 'schedule' },
  })
  const retryNotificationsMutation = useV1QuizAdminPushQuizzesRetryNotificationsCreate()
  const archiveMutation = useV1QuizAdminPushQuizzesArchiveCreate()
  const destroyMutation = useV1QuizAdminPushQuizzesDestroy()
  const updateMutation = useV1QuizAdminPushQuizzesUpdate()
  const patchMutation = useV1QuizAdminPushQuizzesPartialUpdate()
  const pending =
    publishMutation.isPending ||
    scheduleMutation.isPending ||
    cancelScheduleMutation.isPending ||
    cancelMutation.isPending ||
    retryNotificationsMutation.isPending ||
    archiveMutation.isPending ||
    destroyMutation.isPending
  const preview = previewQuery.data?.status === 200 ? previewQuery.data.data : undefined
  const distribution =
    distributionQuery.data?.status === 200 ? distributionQuery.data.data : undefined
  const previewError =
    previewQuery.isError || (previewQuery.data !== undefined && previewQuery.data.status !== 200)
      ? 'Nie udało się przygotować aktualnego podglądu. Spróbuj ponownie.'
      : null

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: getV1QuizAdminPushQuizzesRetrieveQueryKey(uid),
    })
    await queryClient.invalidateQueries({ queryKey: getV1QuizAdminPushQuizzesListQueryKey() })
    // The results resource comes into existence at publish and freezes at
    // archive — keep its cache honest too (T017/T019, FR-019).
    await queryClient.invalidateQueries({
      queryKey: getV1QuizAdminPushQuizzesResultsRetrieveQueryKey(uid),
    })
  }

  const closeAction = () => {
    setAction(null)
    setActionError(null)
    setScheduledAtInput('')
  }

  const runPublish = async () => {
    setActionError(null)
    const response = await publishMutation.mutateAsync({ uid })
    if (response.status >= 400) {
      const message = pushQuizActionError(response.data, response.status)
      setActionError(message)
      setOffendingUids(parseOffendingUids(message))
      return
    }
    setOffendingUids([])
    await refresh()
    closeAction()
  }

  const runArchive = async () => {
    setActionError(null)
    const response = await archiveMutation.mutateAsync({ uid })
    if (response.status >= 400) {
      setActionError(pushQuizActionError(response.data, response.status))
      return
    }
    await refresh()
    closeAction()
  }

  const runCancel = async (reason: string) => {
    setActionError(null)
    const response = await cancelMutation.mutateAsync({ uid, data: { reason } })
    if (response.status >= 400) {
      setActionError(pushQuizActionError(response.data, response.status))
      return
    }
    await refresh()
    closeAction()
  }

  const runSchedule = async () => {
    setActionError(null)
    const response = await scheduleMutation.mutateAsync({
      uid,
      data: { scheduled_at: scheduledAtInput },
    })
    if (response.status >= 400) {
      const message = pushQuizActionError(response.data, response.status)
      setActionError(message)
      setOffendingUids(parseOffendingUids(message))
      return
    }
    setOffendingUids([])
    await refresh()
    closeAction()
  }

  const runCancelSchedule = async () => {
    setActionError(null)
    const response = await cancelScheduleMutation.mutateAsync({ uid })
    if (response.status >= 400) {
      setActionError(pushQuizActionError(response.data, response.status))
      return
    }
    await refresh()
    closeAction()
  }

  // US2/T016 — draft edit is a FULL PUT (the `question_uids` list REPLACES the
  // set, serializer semantics); the form owns the 400 mapping, so just refresh
  // and close on success.
  const handleDraftSave = async (payload: AdminPushQuizWriteRequest) => {
    const response = await updateMutation.mutateAsync({ uid, data: payload })
    if (response.status < 400) {
      await refresh()
      setEditing(false)
    }
    return response
  }

  // T016 — published edit is a PATCH with a body of EXACTLY {name, description}
  // (plan fact #3: any extra key trips the locked-edit 400).
  const handlePublishedSave = async (payload: { name: string; description: string }) => {
    const response = await patchMutation.mutateAsync({ uid, data: payload })
    if (response.status < 400) {
      await refresh()
      setEditing(false)
    }
    return response
  }

  const runRetryNotifications = async () => {
    setActionError(null)
    const response = await retryNotificationsMutation.mutateAsync({ uid })
    if (response.status >= 400) {
      setActionError(pushQuizActionError(response.data, response.status))
      return
    }
    await refresh()
    closeAction()
  }

  const runDelete = async () => {
    setActionError(null)
    const response = await destroyMutation.mutateAsync({ uid })
    if (response.status >= 400) {
      setActionError(pushQuizActionError(response.data, response.status))
      return
    }
    await queryClient.invalidateQueries({ queryKey: getV1QuizAdminPushQuizzesListQueryKey() })
    closeAction()
    await navigate({ to: '/zadania' })
  }

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Szczegóły quizu" />
        <p className="p-6 text-sm text-muted-foreground">Ładowanie…</p>
      </>
    )
  }

  if (!quiz) {
    return (
      <>
        <PageHeader title="Szczegóły quizu" />
        <div className="flex flex-col items-start gap-3 p-6">
          <p className="text-sm text-muted-foreground">Nie znaleziono quizu.</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/zadania">
              <ArrowLeft /> Wróć do listy quizów
            </Link>
          </Button>
        </div>
      </>
    )
  }

  const items = [...quiz.items].sort((a, b) => a.position - b.position)
  const isDraft = state === 'draft'
  const isScheduled = state === 'scheduled'
  const isPublished = state === 'published'
  const isArchived = state === 'archived'
  const isCancelled = state === 'cancelled'
  const hasPublishedSnapshot = isPublished || isArchived || isCancelled
  // A run still pending/in progress finishes on its own; only a published
  // quiz can be resent. See canRetryPushQuizNotifications for the exact
  // status gate (mirrors the server's RETRYABLE_NOTIFICATION_STATUSES).
  const canRetryNotifications = canRetryPushQuizNotifications({
    isPublished,
    publishingEnabled,
    notificationStatus: quiz.notification_status,
  })
  const canPublish = isDraft && items.length > 0 && publishingEnabled
  const scheduleWindowInvalid =
    action === 'schedule' &&
    scheduledAtInput !== '' &&
    quiz.available_until != null &&
    toWarsawDateTimeLocal(quiz.available_until) <= scheduledAtInput
  const scheduleReminderInvalid =
    action === 'schedule' &&
    scheduledAtInput !== '' &&
    quiz.reminder_at != null &&
    toWarsawDateTimeLocal(quiz.reminder_at) <= scheduledAtInput

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon-sm">
              <Link to="/zadania">
                <ArrowLeft />
              </Link>
            </Button>
            <span className="max-w-xl truncate">{quiz.name}</span>
            <PushQuizStateBadge state={quiz.effective_state} />
          </span>
        }
        actions={
          // No affordances until the state is known; the matrix is the state
          // machine (SC-004) and the server backstops every gap. While the
          // edit form is open the lifecycle actions hide (one thing at a time).
          !editing && (
            <span className="flex gap-2">
              {(isDraft || isPublished) && canAuthor && (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil /> Edytuj
                </Button>
              )}
              {isDraft && publishingEnabled && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canPublish}
                  title={canPublish ? undefined : 'Nie można zaplanować quizu bez pytań.'}
                  onClick={() => {
                    setActionError(null)
                    setAction('schedule')
                    void previewQuery.refetch()
                  }}
                >
                  <CalendarClock /> Zaplanuj
                </Button>
              )}
              {isDraft && publishingEnabled && (
                <Button
                  size="sm"
                  disabled={!canPublish}
                  title={canPublish ? undefined : 'Nie można opublikować quizu bez pytań.'}
                  onClick={() => {
                    setActionError(null)
                    setAction('publish')
                    void previewQuery.refetch()
                  }}
                >
                  <Send /> Opublikuj
                </Button>
              )}
              {isScheduled && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setActionError(null)
                    setAction('cancelSchedule')
                  }}
                >
                  <CalendarX /> Anuluj planowanie
                </Button>
              )}
              {isPublished && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setActionError(null)
                    setAction('archive')
                  }}
                >
                  <Archive /> Zarchiwizuj
                </Button>
              )}
              {isPublished && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setActionError(null)
                    setAction('cancel')
                  }}
                >
                  <CircleX /> Anuluj quiz
                </Button>
              )}
              {isDraft && canAuthor && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setActionError(null)
                    setAction('delete')
                  }}
                >
                  <Trash2 /> Usuń szkic
                </Button>
              )}
            </span>
          )
        }
      />

      <div className="flex max-w-4xl flex-col gap-6 p-6">
        <QuizCapabilityNotice capabilities={capabilities} />
        {isArchived && (
          <p className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground">
            Quiz zarchiwizowany — tylko do odczytu.
          </p>
        )}
        {isCancelled && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
            <p className="font-medium text-destructive">Quiz anulowany — tylko do odczytu.</p>
            <p className="mt-1 text-muted-foreground">
              Nowe sesje są zablokowane. Rozpoczęte podejścia można dokończyć, a naliczone punkty
              pozostają bez zmian.
            </p>
          </div>
        )}
        {isScheduled && (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            Quiz jest zaplanowany i zablokowany do edycji. Aby zmienić treść lub termin, anuluj
            planowanie.
          </p>
        )}
        {isDraft && items.length === 0 && !editing && (
          <p className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground">
            Szkic nie zawiera pytań — dodaj co najmniej jedno, aby móc opublikować quiz.
          </p>
        )}

        {editing && canAuthor ? (
          <Card>
            <CardHeader>
              <CardTitle>{isDraft ? 'Edycja szkicu' : 'Edycja nazwy i opisu'}</CardTitle>
            </CardHeader>
            <CardContent>
              {isDraft ? (
                // Draft: everything editable, full PUT replaces the set (US2).
                <PushQuizForm
                  mode="draft-edit"
                  initial={fromQuiz(quiz)}
                  submitLabel="Zapisz zmiany"
                  onSubmit={handleDraftSave}
                  onCancel={() => setEditing(false)}
                />
              ) : (
                // Published: only name + description, PATCH-exact (FR-011).
                <PushQuizForm
                  mode="published-edit"
                  initial={fromQuiz(quiz)}
                  submitLabel="Zapisz zmiany"
                  onSubmit={handlePublishedSave}
                  onCancel={() => setEditing(false)}
                />
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Informacje</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3">
                <Row label="Opis" value={quiz.description || '—'} />
                <Row label="Etykieta modułu" value={quiz.module_label || '—'} />
                <Row
                  label="Limit czasu na pytanie"
                  value={quiz.time_limit_seconds != null ? `${quiz.time_limit_seconds} s` : '—'}
                />
                <Row
                  label="Punktacja"
                  value={pushQuizScoringImpactLabel(quiz.grants_points ?? true)}
                />
                {quiz.grants_points && (
                  <Row
                    label="Zasady punktacji"
                    value={pushQuizScoringConfigLabel(quiz.scoring_config)}
                  />
                )}
                <Row label="Zakres dystrybucji" value={scopeLabel(quiz.scope_kind)} />
                <Row
                  label="Dostępność"
                  value={
                    quiz.available_until
                      ? `do ${formatPolishDateTime(quiz.available_until)}`
                      : 'Bez terminu zakończenia'
                  }
                />
                <Row
                  label="Przypomnienie"
                  value={
                    quiz.reminder_at ? formatPolishDateTime(quiz.reminder_at) : 'Bez przypomnienia'
                  }
                />
                {isScheduled && (
                  <Row
                    label="Zaplanowano publikację"
                    value={quiz.scheduled_at ? formatPolishDateTime(quiz.scheduled_at) : '—'}
                  />
                )}
                {hasPublishedSnapshot && (
                  <>
                    <Row
                      label="Opublikowano"
                      value={quiz.published_at ? formatPolishDateTime(quiz.published_at) : '—'}
                    />
                    {isArchived && (
                      <Row
                        label="Zarchiwizowano"
                        value={quiz.archived_at ? formatPolishDateTime(quiz.archived_at) : '—'}
                      />
                    )}
                    {isCancelled && (
                      <>
                        <Row
                          label="Anulowano"
                          value={quiz.cancelled_at ? formatPolishDateTime(quiz.cancelled_at) : '—'}
                        />
                        <Row label="Powód anulowania" value={quiz.cancellation_reason || '—'} />
                      </>
                    )}
                  </>
                )}
                <Row
                  label="Identyfikator"
                  value={<span className="font-mono text-xs">{quiz.uid}</span>}
                />
                <Row label="Utworzono" value={new Date(quiz.created_at).toLocaleString('pl-PL')} />
                <Row
                  label="Zaktualizowano"
                  value={new Date(quiz.updated_at).toLocaleString('pl-PL')}
                />
              </CardContent>
            </Card>

            {hasPublishedSnapshot && (
              <Card>
                <CardHeader>
                  <CardTitle>Dystrybucja powiadomień</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3">
                  <Row
                    label="Stan wysyłki"
                    value={
                      <span className="flex items-center gap-2">
                        <PushQuizNotificationStatusBadge status={quiz.notification_status} />
                        {canRetryNotifications && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={pending}
                            onClick={() => setAction('retryNotifications')}
                          >
                            <RefreshCw /> Ponów wysyłkę
                          </Button>
                        )}
                      </span>
                    }
                  />
                  <Row
                    label="Próby wysyłki do uczniów"
                    value={<span className="tabular-nums">{quiz.notified_count ?? 0}</span>}
                  />
                  <Row
                    label="Zaakceptowane przez FCM"
                    value={
                      <span className="tabular-nums">{quiz.notification_accepted_count ?? 0}</span>
                    }
                  />
                  <Row
                    label="Błędy obsługi uczniów"
                    value={
                      <span className="tabular-nums">{quiz.notification_error_count ?? 0}</span>
                    }
                  />
                  {failureBreakdownSummary(quiz.notification_failures) && (
                    <Row
                      label="Przyczyny błędów"
                      value={failureBreakdownSummary(quiz.notification_failures)}
                    />
                  )}
                  <Row
                    label="Rozpoczęto"
                    value={
                      quiz.notification_started_at
                        ? new Date(quiz.notification_started_at).toLocaleString('pl-PL')
                        : '—'
                    }
                  />
                  <Row
                    label="Zakończono"
                    value={
                      quiz.notification_finished_at
                        ? new Date(quiz.notification_finished_at).toLocaleString('pl-PL')
                        : '—'
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Akceptacja przez FCM oznacza przyjęcie komunikatu do wysyłki na urządzenie, a
                    nie potwierdzenie jego doręczenia ani wyświetlenia uczniowi.
                  </p>
                </CardContent>
              </Card>
            )}

            {hasPublishedSnapshot && quiz.reminder_at && (
              <Card>
                <CardHeader>
                  <CardTitle>Przypomnienie</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-3">
                  <Row label="Termin" value={formatPolishDateTime(quiz.reminder_at)} />
                  <Row
                    label="Stan"
                    value={<PushQuizReminderStatusBadge status={quiz.reminder_status} />}
                  />
                  <Row
                    label="Próby wysyłki do uczniów"
                    value={
                      <span className="tabular-nums">{quiz.reminder_attempted_count ?? 0}</span>
                    }
                  />
                  <Row
                    label="Zaakceptowane przez FCM"
                    value={
                      <span className="tabular-nums">{quiz.reminder_accepted_count ?? 0}</span>
                    }
                  />
                  <Row
                    label="Błędy obsługi uczniów"
                    value={<span className="tabular-nums">{quiz.reminder_error_count ?? 0}</span>}
                  />
                  {failureBreakdownSummary(quiz.reminder_failures) && (
                    <Row
                      label="Przyczyny błędów"
                      value={failureBreakdownSummary(quiz.reminder_failures)}
                    />
                  )}
                  <Row
                    label="Rozpoczęto"
                    value={
                      quiz.reminder_started_at
                        ? formatPolishDateTime(quiz.reminder_started_at)
                        : '—'
                    }
                  />
                  <Row
                    label="Zakończono"
                    value={
                      quiz.reminder_finished_at
                        ? formatPolishDateTime(quiz.reminder_finished_at)
                        : '—'
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Przypomnienie jest jednorazowe i obejmuje wyłącznie uczniów, którzy nie
                    rozpoczęli quizu. Akceptacja FCM nie jest potwierdzeniem doręczenia.
                  </p>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Zestaw pytań ({items.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {offendingUids.length > 0 && actionErrorForSet(actionError) && (
                  <p className="mb-3 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {actionError}
                  </p>
                )}
                {items.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Brak pytań w zestawie.</p>
                ) : (
                  <ol className="flex flex-col gap-2">
                    {items.map((item) => {
                      const offending = offendingUids.includes(item.question_uid)
                      return (
                        <li
                          key={item.question_uid}
                          className={cn(
                            'flex items-center gap-3 rounded-md border px-3 py-2 text-sm',
                            offending && 'border-destructive/50 bg-destructive/10',
                          )}
                        >
                          <span className="w-6 text-right tabular-nums text-muted-foreground">
                            {item.position + 1}.
                          </span>
                          <span className="w-40 shrink-0 text-muted-foreground">
                            {typeLabel(item.question_type)}
                          </span>
                          <Link
                            to="/pytania/$uid"
                            params={{ uid: item.question_uid }}
                            className="font-mono text-xs underline-offset-4 hover:underline"
                          >
                            {item.question_uid.slice(0, 8)}…
                          </Link>
                          <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground">
                            {item.pinned_revision_number != null
                              ? `rew. ${item.pinned_revision_number}`
                              : '—'}
                          </span>
                          {offending && (
                            <span className="text-xs font-medium text-destructive">
                              Pytanie blokuje publikację
                            </span>
                          )}
                        </li>
                      )
                    })}
                  </ol>
                )}
              </CardContent>
            </Card>

            {/* Results remain readable for every quiz with a published snapshot,
            (FR-014/FR-015) — a draft has no results resource at all. */}
            {resultsEnabled && (
              <ResultsCard
                quizName={quiz.name}
                grantsPoints={quiz.grants_points ?? true}
                results={results}
                loading={resultsQuery.isLoading}
                failed={resultsFailed}
                onRetry={() => void resultsQuery.refetch()}
              />
            )}
          </>
        )}
      </div>

      {/* Publikacja jest poprzedzona serwerowym, key-free podglądem treści i
          aktualnej liczby odbiorców. Dopiero potwierdzenie uruchamia pin + push. */}
      <PushQuizPreviewDialog
        open={action === 'publish' || action === 'schedule'}
        onOpenChange={(open) => {
          if (!open) closeAction()
        }}
        quiz={{
          name: preview?.name ?? quiz.name,
          description: preview?.description ?? quiz.description,
          moduleLabel: preview?.module_label ?? quiz.module_label,
          timeLimitSeconds: preview?.time_limit_seconds ?? quiz.time_limit_seconds,
          grantsPoints: preview?.grants_points ?? quiz.grants_points ?? true,
          scoringConfig: preview?.scoring_config ?? quiz.scoring_config,
          availableUntil: preview?.available_until ?? quiz.available_until,
          reminderAt: preview?.reminder_at ?? quiz.reminder_at,
          audienceLabel: scopeLabel(preview?.scope_kind ?? quiz.scope_kind),
        }}
        recipientCount={preview?.recipient_count ?? 0}
        dynamicAudienceNote={
          action === 'schedule'
            ? 'To aktualna liczba aktywnych uczniów. Ostateczna grupa odbiorców zostanie wyznaczona w chwili automatycznej publikacji.'
            : 'To aktualna liczba aktywnych uczniów. Przynależność do szkoły lub klasy jest rozstrzygana na bieżąco, także po publikacji.'
        }
        questions={preview ? toPreviewQuestions(preview) : []}
        ready={
          preview !== undefined &&
          previewError === null &&
          (action !== 'schedule' ||
            (scheduledAtInput !== '' && !scheduleWindowInvalid && !scheduleReminderInvalid))
        }
        loading={previewQuery.isFetching}
        pending={pending}
        error={actionError ?? previewError}
        onRetry={() => void previewQuery.refetch()}
        onConfirm={() => void (action === 'schedule' ? runSchedule() : runPublish())}
        title={action === 'schedule' ? 'Zaplanuj publikację' : undefined}
        description={
          action === 'schedule'
            ? 'Wybierz termin i sprawdź treść quizu. Powiadomienia zostaną wysłane dopiero przy automatycznej publikacji.'
            : undefined
        }
        confirmLabel={action === 'schedule' ? 'Zaplanuj quiz' : undefined}
        pendingLabel={action === 'schedule' ? 'Planowanie…' : undefined}
        beforeSummary={
          <>
            <DistributionWarnings preview={distribution} />
            {action === 'schedule' ? (
              <div className="grid gap-2 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <Label htmlFor="push-quiz-scheduled-at">Termin publikacji</Label>
                <Input
                  id="push-quiz-scheduled-at"
                  type="datetime-local"
                  value={scheduledAtInput}
                  onChange={(event) => {
                    setScheduledAtInput(event.target.value)
                    setActionError(null)
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Termin jest interpretowany jako czas polski (Europe/Warsaw). Po zaplanowaniu treść
                  quizu zostanie zablokowana.
                </p>
                {scheduleWindowInvalid && (
                  <p className="text-sm text-destructive">
                    Termin zakończenia dostępności musi przypadać po publikacji.
                  </p>
                )}
                {scheduleReminderInvalid && (
                  <p className="text-sm text-destructive">
                    Przypomnienie musi przypadać po publikacji.
                  </p>
                )}
              </div>
            ) : null}
          </>
        }
      />

      <ConfirmDialog
        open={action === 'cancelSchedule'}
        onOpenChange={(open) => (open ? setAction('cancelSchedule') : closeAction())}
        title="Anulować planowanie?"
        description={
          <>
            Quiz wróci do szkicu i ponownie będzie można edytować pytania, zakres oraz termin.
            Powiadomienia nie zostaną wysłane.
          </>
        }
        confirmLabel="Anuluj planowanie"
        pending={pending}
        error={actionError}
        onConfirm={() => void runCancelSchedule()}
      />

      <PushQuizCancelDialog
        open={action === 'cancel'}
        onOpenChange={(open) => (open ? setAction('cancel') : closeAction())}
        pending={pending}
        error={actionError}
        onConfirm={(reason) => void runCancel(reason)}
      />

      {/* 079 A2: the only way out of a fan-out that reached nobody. Safe to press
          twice — the backend skips every student FCM already accepted. */}
      <ConfirmDialog
        open={action === 'retryNotifications'}
        onOpenChange={(open) => (open ? setAction('retryNotifications') : closeAction())}
        title="Ponowić wysyłkę powiadomień?"
        description={
          <>
            <p>
              Nowe powiadomienie dostaną uczniowie, u których poprzednia próba nie dotarła z
              powodu błędu wysyłki, oraz uczniowie, do których wysyłka jeszcze nie ruszyła
              (przerwana wysyłka, nowe klasy w zakresie) — nikt nie otrzyma go dwa razy, a
              liczniki narastają, zamiast być nadpisane. Uczniowie bez zarejestrowanego
              urządzenia, bez zgody na powiadomienia w telefonie oraz odrzuceni przez FCM nie
              zostaną ponowieni. Przypomnienie nie jest ponawiane: jest jednorazowe z założenia.
            </p>
            {failureBreakdownSummary(quiz.notification_failures) && (
              <p className="mt-2 text-sm text-muted-foreground">
                Ostatnie niepowodzenia: {failureBreakdownSummary(quiz.notification_failures)}
              </p>
            )}
          </>
        }
        confirmLabel="Ponów wysyłkę"
        pending={pending}
        error={actionError}
        onConfirm={() => void runRetryNotifications()}
      />

      {/* US3 — archive (FR-012): final, but honest about in-flight sessions
          (backend P9/P13 — an open session completes on the pinned snapshot). */}
      <ConfirmDialog
        open={action === 'archive'}
        onOpenChange={(open) => (open ? setAction('archive') : closeAction())}
        title="Zarchiwizować quiz?"
        description={
          <>
            Archiwizacja jest ostateczna — quiz przestanie być dostępny dla nowych uczestników
            (rozpoczęte podejścia można dokończyć), a wyniki pozostaną dostępne tylko do odczytu.
          </>
        }
        confirmLabel="Zarchiwizuj"
        destructive
        pending={pending}
        error={actionError}
        onConfirm={() => void runArchive()}
      />

      {/* US3 — delete (FR-013): drafts only; published quizzes use terminal actions. */}
      <ConfirmDialog
        open={action === 'delete'}
        onOpenChange={(open) => (open ? setAction('delete') : closeAction())}
        title="Usunąć szkic?"
        description={<>Szkic zostanie trwale usunięty.</>}
        confirmLabel="Usuń"
        destructive
        pending={pending}
        error={actionError}
        onConfirm={() => void runDelete()}
      />
    </>
  )
}

// The set-level banner shows only for offender-naming refusals; other action
// errors already render inside the open ConfirmDialog.
function actionErrorForSet(error: string | null): error is string {
  return error !== null
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[12rem_1fr] gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  )
}

function toPreviewQuestions(preview: AdminPushQuizPreview): PushQuizPreviewQuestion[] {
  return preview.questions.map((question) => {
    if (question.type === 'pairs') {
      return {
        uid: question.uid,
        type: question.type,
        difficultyLabel: difficultyLabel(question.difficulty),
        title: question.title,
        terms: question.terms,
        definitions: question.definitions,
      }
    }
    if (question.type === 'singleChoice') {
      return {
        uid: question.uid,
        type: question.type,
        difficultyLabel: difficultyLabel(question.difficulty),
        prompt: question.prompt,
        options: question.options,
      }
    }
    return {
      uid: question.uid,
      type: question.type,
      difficultyLabel: difficultyLabel(question.difficulty),
      prompt: question.prompt,
    }
  })
}
